/**
 * Polaris IndexTable of variants sold in the last 90 days.
 *
 * Sister of `CogsTable` (saved entries) but optimised for discoverability:
 * shows EVERY sold variant — including ones that don't yet have a saved
 * cost — so merchants can add costs inline without having to search for
 * each product first.
 *
 * Inline-editable cost cell follows the CogsTable pattern (TextField; save
 * on blur; error state inline). Free-tier cap violations bubble up via
 * `onSave` returning `{ ok: false, code: "COGS_CAP_EXCEEDED" }`; the cap
 * banner above the table covers the user-facing copy.
 */

import { useState } from "react";
import {
  Badge,
  Box,
  Card,
  EmptyState,
  IndexTable,
  InlineStack,
  Text,
  TextField,
  Thumbnail,
} from "@shopify/polaris";
import type { Money, SoldVariantRow } from "@fbc/shared";
import { formatMoney, formatNumber } from "../lib/format.js";

type Props = {
  variants: SoldVariantRow[];
  shopCurrency: string;
  defaultMarginPct: number;
  onSave: (
    variantId: string,
    productId: string,
    title: string,
    sku: string | null,
    cost: Money,
  ) => Promise<{ ok: true } | { ok: false; code: string; message: string }>;
};

type RowEditState = {
  amount: string;
  saving: boolean;
  error: string | null;
  // Tracks the last-known-good saved value so we can debounce a no-op blur.
  lastSavedAmount: string | null;
};

export function SoldProductsTable({ variants, shopCurrency, defaultMarginPct, onSave }: Props) {
  const [edits, setEdits] = useState<Record<string, RowEditState>>({});
  const resourceName = { singular: "product", plural: "products" };

  if (variants.length === 0) {
    return (
      <Card>
        <EmptyState
          heading="No products sold in the last 90 days"
          image="data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%221%22%20height%3D%221%22/%3E"
          fullWidth
        >
          <p>
            Once orders come in, they'll appear here so you can add their costs.
          </p>
        </EmptyState>
      </Card>
    );
  }

  const setEdit = (variantId: string, patch: Partial<RowEditState>) =>
    setEdits((s) => {
      const previous = s[variantId];
      return {
        ...s,
        [variantId]: {
          amount: previous?.amount ?? "",
          saving: previous?.saving ?? false,
          error: previous?.error ?? null,
          lastSavedAmount: previous?.lastSavedAmount ?? null,
          ...patch,
        },
      };
    });

  const handleBlur = async (row: SoldVariantRow) => {
    const edit = edits[row.variant_id];
    if (!edit || edit.amount === "") return;
    const initial = row.existing_cost?.amount ?? edit.lastSavedAmount ?? "";
    if (edit.amount === initial) return;
    const num = Number(edit.amount);
    if (!Number.isFinite(num) || num < 0) {
      setEdit(row.variant_id, { error: "Enter a valid number" });
      return;
    }
    setEdit(row.variant_id, { saving: true, error: null });
    const result = await onSave(row.variant_id, row.product_id, row.title, row.sku, {
      amount: edit.amount,
      currency_code: shopCurrency,
    });
    setEdit(row.variant_id, { saving: false });
    if (!result.ok) {
      const message =
        result.code === "COGS_CAP_EXCEEDED"
          ? "Free plan cap reached. Upgrade to Pro to add this cost."
          : result.message;
      setEdit(row.variant_id, { error: message });
    } else {
      setEdit(row.variant_id, { error: null, lastSavedAmount: edit.amount });
    }
  };

  const rows = variants.map((row, index) => {
    const edit = edits[row.variant_id];
    // Show the in-progress edit if any; otherwise show the most recently saved
    // value (either via this session's save or from server).
    const value =
      edit?.amount !== undefined && edit.amount !== ""
        ? edit.amount
        : edit?.lastSavedAmount ?? row.existing_cost?.amount ?? "";
    const isSet =
      Boolean(row.existing_cost) || Boolean(edit?.lastSavedAmount);

    return (
      <IndexTable.Row id={row.variant_id} key={row.variant_id} position={index}>
        <IndexTable.Cell>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <Thumbnail source={row.image ?? ""} alt="" size="small" />
            <Box>
              <Text as="span" fontWeight="medium">
                {row.title}
              </Text>
              {row.sku ? (
                <Text as="p" tone="subdued" variant="bodySm">
                  SKU: {row.sku}
                </Text>
              ) : null}
            </Box>
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span" tone="subdued">
            {row.price ? formatMoney(row.price) : "—"}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text as="span" alignment="end">
            {formatNumber(row.units_sold)}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <div style={{ maxWidth: "140px" }}>
            <TextField
              label={`Cost for ${row.title}`}
              labelHidden
              type="number"
              value={value}
              onChange={(v) => setEdit(row.variant_id, { amount: v, error: null })}
              onBlur={() => void handleBlur(row)}
              prefix={shopCurrency}
              step={0.01}
              min={0}
              autoComplete="off"
              error={edit?.error ?? undefined}
              disabled={edit?.saving}
            />
          </div>
        </IndexTable.Cell>
        <IndexTable.Cell>
          {isSet ? (
            <Badge tone="success">Set</Badge>
          ) : (
            <Badge tone="attention">Not set</Badge>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Card padding="0">
      <IndexTable
        resourceName={resourceName}
        itemCount={variants.length}
        selectable={false}
        headings={[
          { title: "Product" },
          { title: "Price" },
          { title: "Units sold (90d)", alignment: "end" },
          { title: "Cost" },
          { title: "Status" },
        ]}
      >
        {rows}
      </IndexTable>
      {defaultMarginPct > 0 ? (
        <Box padding="300">
          <Text as="p" tone="subdued" variant="bodySm">
            Products without a saved cost are estimated using a {(defaultMarginPct * 100).toFixed(1)}
            % default margin.
          </Text>
        </Box>
      ) : null}
    </Card>
  );
}
